import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/layout/Breadcrumbs";
import SectionHeading from "../components/blocks/SectionHeading";
import CTASection from "../components/blocks/CTASection";
import EnquiryForm from "../components/blocks/EnquiryForm";

const FIVE_QUESTIONS = [
  "Where are we creating value?",
  "Where are we destroying value?",
  "Where is our cash going?",
  "What is preventing the organisation from scaling?",
  "What will make the enterprise more valuable?",
];

const ADVISORY_AREAS = [
  { title: "Business Economics & Value Creation", text: "Understanding the economics underneath revenue — cash, margin, profitability, capital and enterprise value." },
  { title: "Founder-Led Business Transformation", text: "Moving from founder dependency to management capability to institutional capability." },
  { title: "Strategic Growth", text: "From opportunity-driven growth to disciplined, economically sound growth." },
  { title: "Executive & Leadership Advisory", text: "Working directly with founders, promoters and CEOs on the decisions only they can make." },
  { title: "Business Professionalisation", text: "The systems, governance, management disciplines and leadership structures the next stage requires." },
];

const APPROACH = [
  { n: "01", t: "Diagnose", d: "A rigorous look beneath the topline: cash conversion, margins, customer profitability, revenue quality, concentration and founder dependency." },
  { n: "02", t: "Advise", d: "Clear strategic choices — where value is created and destroyed, and what will make the enterprise stronger, not just bigger." },
  { n: "03", t: "Build", d: "Practical execution frameworks, leadership development and professionalisation — carried out with the Institute's education and research capability behind it." },
];

const ADVISORY_FIELDS = [
  { name: "name", label: "Name", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone", type: "tel" },
  { name: "organisation", label: "Organisation", required: true },
  { name: "role", label: "Role", required: true },
  { name: "stage", label: "Business Stage", type: "select", options: ["Early-stage", "Founder-led growth", "Growth-stage", "Mid-market", "Established enterprise"] },
  { name: "challenge", label: "The Challenge You Are Facing", type: "textarea", full: true, required: true },
  { name: "timeline", label: "Timeline", type: "select", options: ["Immediately", "Within 3 months", "3–6 months", "Exploratory"] },
];

export default function AdvisoryPage() {
  return (
    <>
      <SEO
        title="The Vantage Group™ — Strategic Business & Leadership Advisory"
        description="The Vantage Group™ is a strategic business and leadership advisory serving founders, promoters, CEOs and leadership teams of founder-led, growth-stage and mid-market businesses — focused on cash, profitability, professionalisation and enterprise value."
        path="/advisory"
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Advisory — The Vantage Group™" }]} />

      <section className="bg-navy">
        <div className="container-x py-16 sm:py-24">
          <p className="eyebrow-light">The Vantage Group™</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
            Not merely bigger. Economically stronger, strategically clearer, more valuable.
          </h1>
          <span className="gold-rule mt-8 !w-24" aria-hidden="true" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-platinum md:text-lg">
            The Vantage Group™ is a strategic business and leadership advisory serving founders, promoters, CEOs
            and leadership teams of founder-led, growth-stage and mid-market businesses — combining strategic
            thinking, business diagnostics, leadership advisory, financial and operating perspectives, and
            practical execution frameworks.
          </p>
          <a href="#advisory-enquiry" className="btn-gold mt-10 inline-flex" data-testid="advisory-hero-cta">
            Request an Advisory Conversation
          </a>
        </div>
      </section>

      <section className="container-x py-16 sm:py-24" data-testid="advisory-questions">
        <div className="grid items-start gap-10 lg:grid-cols-2">
          <SectionHeading
            eyebrow="The Advisory Lens"
            title="Five questions every engagement answers."
            intro="The Group's philosophy is centred on helping businesses answer five fundamental questions — honestly, and with the numbers to prove it."
          />
          <ol className="space-y-3">
            {FIVE_QUESTIONS.map((q, i) => (
              <li key={q} className="flex items-center gap-4 border border-platinum bg-paper px-5 py-4" data-testid={`advisory-question-${i + 1}`}>
                <span className="font-display text-xl font-semibold text-gold-deep" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-sub text-sm font-semibold text-navy">{q}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-paper" data-testid="advisory-areas">
        <div className="container-x py-16 sm:py-24">
          <SectionHeading
            eyebrow="Areas of Advisory"
            title="Where the Group works."
            intro="Advisory is delivered by founder Rakesh Gopinathan and focused on the drivers that determine business quality."
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {ADVISORY_AREAS.map((a) => (
              <div key={a.title} className="card card-hover" data-testid={`advisory-area-${a.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
                <h3 className="font-sub text-base font-semibold text-navy">{a.title}</h3>
                <span className="gold-rule mt-3 !w-10" aria-hidden="true" />
                <p className="mt-3 text-sm leading-relaxed text-slate">{a.text}</p>
              </div>
            ))}
            <div className="card card-hover border-gold/60 bg-navy" data-testid="advisory-area-founder">
              <h3 className="font-sub text-base font-semibold text-white">Meet the Advisor</h3>
              <span className="gold-rule mt-3 !w-10" aria-hidden="true" />
              <p className="mt-3 text-sm leading-relaxed text-platinum">
                Rakesh Gopinathan — Founder & CEO of The Vantage Group™, Founder of Vantage Institute™ and
                Wowrakesh Business Academy®.
              </p>
              <Link to="/founder/rakesh-gopinathan" className="mt-4 inline-flex min-h-[44px] items-center font-sub text-xs font-semibold uppercase tracking-[0.12em] text-gold transition-colors hover:text-gold-soft" data-testid="advisory-founder-link">
                Read the Founder Profile
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-16 sm:py-24" data-testid="advisory-approach">
        <SectionHeading eyebrow="How Engagements Work" title="Diagnose. Advise. Build." />
        <ol className="mt-12 grid gap-5 sm:grid-cols-3">
          {APPROACH.map((s) => (
            <li key={s.n} className="border-t-2 border-gold pt-6" data-testid={`advisory-step-${s.n}`}>
              <p className="font-display text-2xl font-semibold text-platinum" aria-hidden="true">{s.n}</p>
              <h3 className="mt-3 font-sub text-base font-semibold text-navy">{s.t}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-slate">{s.d}</p>
            </li>
          ))}
        </ol>
        <p className="mt-10 max-w-3xl text-sm leading-relaxed text-slate">
          The Group advises; the{" "}
          <Link to="/about" className="text-link" data-testid="advisory-institute-link">Institute builds the capability</Link>{" "}
          to carry that advice through — the two are designed to work as one ecosystem.
        </p>
      </section>

      <section id="advisory-enquiry" className="container-x scroll-mt-24 pb-16 sm:pb-24" data-testid="advisory-enquiry-section">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <SectionHeading
            eyebrow="Start the Conversation"
            title="Request an advisory conversation."
            intro="Tell us about your business and the challenge in front of you. The conversation begins with diagnosis, not a pitch."
          />
          <div className="card">
            <EnquiryForm
              fields={ADVISORY_FIELDS}
              submitLabel="Request an Advisory Conversation"
              testId="advisory-enquiry-form"
            />
          </div>
        </div>
      </section>

      <CTASection
        title="Prefer to build capability inside your organisation?"
        text="Explore the Institute's corporate programmes — the education arm of the same ecosystem."
        primaryLabel="For Organisations"
        primaryTo="/organisations"
        secondaryLabel="Talk to Vantage"
        secondaryTo="/contact"
        testId="advisory-cta"
      />
    </>
  );
}
